/**
 * Ambient Runtime Store (AMB-0)
 *
 * Zustand store for Settings → Ambient Runtime: job schedules, channel policy,
 * delivery log, and run history. DB-first — every write goes through the
 * ambientRuntimeService before local state updates.
 */

import { create } from 'zustand';
import {
  getAmbientJobs,
  getAmbientChannelPolicy,
  getAmbientDeliveries,
  getAmbientRunLogs,
  setAmbientJobEnabled,
  saveAmbientChannelPolicy,
  runAmbientJobNow,
  defaultChannelPolicy,
  type AmbientJob,
  type AmbientJobType,
  type AmbientChannelPolicy,
  type AmbientDelivery,
  type AmbientRunLog,
} from '../../lib/ambientRuntimeService';

interface AmbientRuntimeState {
  jobs: AmbientJob[];
  policy: AmbientChannelPolicy | null;
  deliveries: AmbientDelivery[];
  runLogs: AmbientRunLog[];
  isLoading: boolean;
  isSaving: boolean;
  runningJobType: AmbientJobType | null;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchAll: (workspaceId: string, force?: boolean) => Promise<void>;
  refreshActivity: (workspaceId: string) => Promise<void>;
  toggleJob: (
    workspaceId: string,
    jobType: AmbientJobType,
    enabled: boolean,
    userId?: string,
  ) => Promise<{ success: boolean }>;
  savePolicy: (
    policy: AmbientChannelPolicy,
    userId?: string,
  ) => Promise<{ success: boolean }>;
  runNow: (workspaceId: string, jobType: AmbientJobType) => Promise<{ success: boolean; error?: string }>;
}

export const useAmbientRuntimeStore = create<AmbientRuntimeState>((set, get) => ({
  jobs: [],
  policy: null,
  deliveries: [],
  runLogs: [],
  isLoading: false,
  isSaving: false,
  runningJobType: null,
  error: null,
  loadedWorkspaceId: null,

  fetchAll: async (workspaceId, force = false) => {
    const s = get();
    if (!force && s.loadedWorkspaceId === workspaceId && s.policy !== null) return;
    set({ isLoading: true, error: null });

    const [jobs, policy, deliveries, runLogs] = await Promise.all([
      getAmbientJobs(workspaceId),
      getAmbientChannelPolicy(workspaceId),
      getAmbientDeliveries(workspaceId),
      getAmbientRunLogs(workspaceId),
    ]);
    const firstError = jobs.error ?? policy.error ?? deliveries.error ?? runLogs.error;

    set({
      jobs: jobs.data,
      policy: policy.data,
      deliveries: deliveries.data,
      runLogs: runLogs.data,
      isLoading: false,
      error: firstError,
      loadedWorkspaceId: workspaceId,
    });
  },

  refreshActivity: async (workspaceId) => {
    const [jobs, deliveries, runLogs] = await Promise.all([
      getAmbientJobs(workspaceId),
      getAmbientDeliveries(workspaceId),
      getAmbientRunLogs(workspaceId),
    ]);
    set({ jobs: jobs.data, deliveries: deliveries.data, runLogs: runLogs.data });
  },

  toggleJob: async (workspaceId, jobType, enabled, userId) => {
    set({ isSaving: true });
    const { data, error } = await setAmbientJobEnabled({
      workspaceId,
      jobType,
      enabled,
      createdBy: userId,
    });
    if (error || !data) {
      set({ isSaving: false, error: error ?? 'Failed to save job' });
      return { success: false };
    }
    set((s) => ({
      isSaving: false,
      error: null,
      jobs: s.jobs.some((j) => j.jobType === jobType)
        ? s.jobs.map((j) => (j.jobType === jobType ? data : j))
        : [...s.jobs, data].sort((a, b) => a.jobType.localeCompare(b.jobType)),
    }));
    return { success: true };
  },

  savePolicy: async (policy, userId) => {
    set({ isSaving: true });
    const { data, error } = await saveAmbientChannelPolicy(policy, userId);
    if (error || !data) {
      set({ isSaving: false, error: error ?? 'Failed to save channel policy' });
      return { success: false };
    }
    set({ isSaving: false, error: null, policy: data });
    return { success: true };
  },

  runNow: async (workspaceId, jobType) => {
    set({ runningJobType: jobType });
    const { error } = await runAmbientJobNow(workspaceId, jobType);
    set({ runningJobType: null });
    if (error) return { success: false, error };
    await get().refreshActivity(workspaceId);
    return { success: true };
  },
}));

export { defaultChannelPolicy };
